// Decision helpers for the create-organisation submit. User-fixable faults surface
// inline and never count toward escalation; only genuine system failures do.

export const SYSTEM_FAILURE_CAP = 3;

// Only 5xx / network / session-switch rejections increment the count that drives this.
export function shouldEscalate(systemFailureCount: number): boolean {
  return systemFailureCount >= SYSTEM_FAILURE_CAP;
}

export interface CreateOrgErrorView {
  field: 'name' | 'slug' | null;
  system: boolean; // true => counts toward the escalation cap
  message: string;
}

// Network (status 0) and 5xx are genuine system failures; an unrecognised 4xx is
// caller-fixable (e.g. a re-auth 401, or a future input fault) and must not burn an
// escalation strike on a problem support can't resolve.
function isSystemStatus(status: number): boolean {
  return status === 0 || status >= 500;
}

// `code` is the ErrorResponse.error fault code from the API.
export function mapCreateOrgError(status: number, code: string): CreateOrgErrorView {
  switch (code) {
    case 'slug_taken':
      return { field: 'slug', system: false, message: 'That URL is already taken — try another.' };
    case 'slug_reserved':
      return { field: 'slug', system: false, message: 'That URL is reserved — please choose another.' };
    case 'slug_missing':
    case 'slug_malformed':
    case 'slug_too_long':
    case 'slug_invalid': // the server's own format-fault code (OrganisationsEndpoint)
      return { field: 'slug', system: false, message: 'That URL isn’t valid — use lowercase letters, numbers and hyphens.' };
    case 'invalid_name':
      return { field: 'name', system: false, message: 'That organisation name isn’t valid.' };
    default:
      return { field: null, system: isSystemStatus(status), message: 'Something went wrong setting up your organisation. Please try again.' };
  }
}
